import { createSlice } from '@reduxjs/toolkit';
import { appClient } from 'api/apiClient';
import { buildPageQuery, type Paginated } from 'api/v1Helpers';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import type { RootState } from 'redux/store';
import { normalizeParish, type Parish, type ParishPublic } from 'types/parishes';

interface ParishListState {
    data: Parish[];
    loading: boolean;
    error: string | null;
    total: number;
    /** requestId of the latest dispatched fetch — drops an older request's response that
     *  resolves after a newer one (e.g. rapid Next-Page clicks), matching the guard pattern
     *  used by parishMemberSlice / announcementsSlice / staffSlice. */
    requestId: string | null;
}

const initialState: ParishListState = {
    data: [],
    loading: false,
    error: null,
    total: 0,
    requestId: null,
};

interface FetchParishDataPayload {
    searchText?: string;
    /** When set, load children of this parish instead of selectedParishId. */
    parentParishId?: string;
}

export const fetchParishData = createAsyncThunkWrapper<{ data: Parish[]; total: number } | undefined, FetchParishDataPayload>(
    'parish/fetchParishData',
    async ({ searchText, parentParishId }, { getState }) => {
        // Read pageNumber/pageSize fresh from the shared table slice at execution time (not as a
        // caller-supplied argument) — a caller-supplied value is a snapshot from the component's
        // render closure, which can be stale on the very first render after a parish switch (the
        // reset dispatch that follows lands one render later) or on a fresh mount carrying over a
        // leftover page number from a different screen. Reading here instead means this always
        // reflects whatever the table slice actually holds by the moment this thunk runs,
        // including any reset dispatched moments earlier in the same synchronous pass.
        const state = getState() as RootState;
        const { pageSize = 20, pageNumber = 0 } = state?.app?.table || {};
        // /v1 uses 1-based page; UI still passes 0-based pageNumber.
        const page = Math.max(1, pageNumber + 1);
        const search = (searchText || '').trim();

        if (search) {
            const qs = buildPageQuery(page, pageSize, { search });
            const response = await appClient.get<Paginated<ParishPublic>>(`parishes?${qs}`);
            const items = ((response?.items || []) as ParishPublic[]).map(normalizeParish);
            return { data: items, total: response?.total ?? items.length };
        }

        const parentId = parentParishId || state?.user?.selectedParishId;
        if (parentId) {
            // `parishes/:id/children` ignores page/pageSize entirely (backend limitation — see
            // docs/tickets/resolved/2026-09-01-WEB-parishes-pagination-fix.md) and always returns
            // the full list. Slice it client-side here so AtlantaParishes's serverSidePagination
            // still behaves correctly (a real per-page slice) instead of rendering the identical
            // full list on every page, now that StyledTable no longer re-slices on top of it.
            const response = await appClient.get<Paginated<ParishPublic>>(`parishes/${parentId}/children`);
            const items = ((response?.items || []) as ParishPublic[]).map(normalizeParish);
            const total = response?.total ?? items.length;
            const start = pageNumber * pageSize;
            return { data: items.slice(start, start + pageSize), total };
        }

        const qs = buildPageQuery(page, pageSize);
        const response = await appClient.get<Paginated<ParishPublic>>(`parishes?${qs}`);
        const items = ((response?.items || []) as ParishPublic[]).map(normalizeParish);
        return { data: items, total: response?.total ?? items.length };
    }
);

const parishSlice = createSlice({
    name: 'parish',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchParishData.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.requestId = action.meta.requestId;
        });
        builder.addCase(fetchParishData.fulfilled, (state, action) => {
            // Drop a response for a request that's since been superseded (e.g. a second
            // Next-Page click fired before the first request resolved) so a slower, older
            // response can't overwrite newer data.
            if (action.meta.requestId !== state.requestId) return;
            state.loading = false;
            const payload = action.payload;
            if (!payload) return;
            state.total = payload.total;
            state.data = payload.data || [];
        });
        builder.addCase(fetchParishData.rejected, (state, action) => {
            if (action.meta.requestId !== state.requestId) return;
            state.loading = false;
            state.error = action?.error?.message || null;
        });
    },
});

export default parishSlice.reducer;
